import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Table, TableWrapper } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { StateMachine } from "@/components/data-display/state-machine";
import { cn } from "@/lib/utils";
import { usePermission } from "@/hooks/use-permission";
import { DEP_PREFLIGHT_CHECKS, DEP_TABLE } from "@/data/mock";
import type { DepPreflightCheck, PreflightStatus } from "@/types";

const STATUS_PILL: Record<PreflightStatus, { chip: string; label: string }> = {
  passed: { chip: "bg-success-soft text-success", label: "Passed" },
  failed: { chip: "bg-danger-soft text-danger", label: "Failed" },
  pending: { chip: "bg-warning-soft text-warning", label: "Pending" },
};

function MetricCard({ value, label, valueClass }: { value: number; label: string; valueClass: string }) {
  return (
    <div className="flex flex-col items-start gap-1 rounded-[10px] border border-line bg-surface px-5 py-[18px]">
      <div className={cn("text-[28px] font-bold leading-[1.1] tracking-tight", valueClass)}>{value}</div>
      <div className="text-xs font-medium text-muted">{label}</div>
    </div>
  );
}

function PreflightRow({
  check,
  onRetry,
  onDetail,
}: {
  check: DepPreflightCheck;
  onRetry: () => void;
  onDetail: (name: string) => void;
}) {
  const pill = STATUS_PILL[check.status];
  return (
    <tr className="border-t border-line hover:bg-bg">
      <td className="px-5 py-[10px] whitespace-nowrap text-[12px] text-ink">{check.name}</td>
      <td className="px-5 py-[10px]">
        <span
          className={cn(
            "inline-flex items-center rounded-full px-2 py-[3px] text-[10px] font-semibold leading-none whitespace-nowrap",
            pill.chip
          )}
        >
          {pill.label}
        </span>
      </td>
      <td className="px-5 py-[10px] whitespace-nowrap text-[12px] text-ink">{check.lastChecked}</td>
      <td className="px-5 py-[10px] whitespace-nowrap text-[12px] text-ink">{check.owner}</td>
      <td className="px-5 py-[10px]">
        {check.retryable && check.status === "failed" ? (
          <button
            type="button"
            onClick={onRetry}
            className="text-left text-[11px] font-semibold text-danger outline-none transition-colors hover:underline focus-visible:ring-2 focus-visible:ring-primary-soft"
          >
            Retry
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onDetail(check.name)}
            className="text-left text-[11px] font-semibold text-primary outline-none transition-colors hover:underline focus-visible:ring-2 focus-visible:ring-primary-soft"
          >
            Detail
          </button>
        )}
      </td>
    </tr>
  );
}

export default function DeploymentPage() {
  const canManage = usePermission("admin:access");
  const [tab, setTab] = useState("preflight");
  const [hsmFixed, setHsmFixed] = useState(false);
  const [rerunning, setRerunning] = useState(false);

  const checks = useMemo(() => {
    if (!hsmFixed) return DEP_PREFLIGHT_CHECKS;
    return DEP_PREFLIGHT_CHECKS.map((c) => (c.id === "hsm" ? { ...c, status: "passed" as const } : c));
  }, [hsmFixed]);

  const passed = useMemo(() => checks.filter((c) => c.status === "passed").length, [checks]);
  const pending = useMemo(() => checks.filter((c) => c.status === "pending").length, [checks]);
  const failed = useMemo(() => checks.filter((c) => c.status === "failed").length, [checks]);
  const blocking = failed > 0;

  const fixHSM = () => setHsmFixed(true);

  const showDetail = (name: string) => {
    window.alert(`${name} — verification details are recorded in the audit log.`);
  };

  const runPreflight = () => {
    if (rerunning) return;
    setRerunning(true);
    window.setTimeout(() => setRerunning(false), 1500);
  };

  return (
    <div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="preflight">Preflight</TabsTrigger>
          <TabsTrigger value="bootstrap">Bootstrap</TabsTrigger>
          <TabsTrigger value="acceptance">Acceptance</TabsTrigger>
          <TabsTrigger value="operate">Operate</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <div className="mt-5">
          {tab === "preflight" && (
            <TabsContent value="preflight" className="space-y-5">
              <div className="flex flex-col gap-1.5">
                <h1 className="text-2xl font-bold leading-[1.2] text-ink">Preflight Checklist</h1>
                <p className="text-xs text-muted">DEP-01 · DEP-001 · Harbor Trust Bank, N.A.</p>
                <StateMachine current="preflight-running" />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <MetricCard value={passed} label="Passed" valueClass="text-success" />
                <MetricCard value={pending} label="Pending" valueClass="text-warning" />
                <MetricCard value={failed} label="Failed" valueClass="text-danger" />
              </div>

              {blocking && (
                <div className="flex items-start gap-2.5 rounded-sm border-l-4 border-warning bg-warning-soft px-4 py-3 text-sm text-warning">
                  <span aria-hidden className="mt-0.5 shrink-0 leading-none">⚠</span>
                  <div>
                    <strong className="block font-bold">1 blocking issue</strong>
                    HSM / KMS profile not configured. Retry the check from the table below and resolve before
                    proceeding.
                  </div>
                </div>
              )}

              <TableWrapper className="rounded-[10px]">
                <Table>
                  <thead>
                    <tr className="border-b border-line bg-bg">
                      <th className="px-5 py-[10px] text-left text-[11px] font-semibold normal-case tracking-normal text-muted">Dependency</th>
                      <th className="px-5 py-[10px] text-left text-[11px] font-semibold normal-case tracking-normal text-muted">Status</th>
                      <th className="px-5 py-[10px] text-left text-[11px] font-semibold normal-case tracking-normal text-muted">Last Checked</th>
                      <th className="px-5 py-[10px] text-left text-[11px] font-semibold normal-case tracking-normal text-muted">Owner</th>
                      <th className="px-5 py-[10px] text-left text-[11px] font-semibold normal-case tracking-normal text-muted">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {checks.map((c) => (
                      <PreflightRow key={c.id} check={c} onRetry={fixHSM} onDetail={showDetail} />
                    ))}
                  </tbody>
                </Table>
              </TableWrapper>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={blocking || !canManage}
                  onClick={() => setTab("bootstrap")}
                  className={cn(
                    "inline-flex h-[36px] items-center gap-1 rounded-[8px] px-4 text-sm font-medium transition-colors",
                    blocking || !canManage
                      ? "cursor-not-allowed bg-bg text-subtle"
                      : "bg-primary text-on-primary hover:bg-primary-hover"
                  )}
                >
                  Proceed to Bootstrap
                </button>
                <button
                  type="button"
                  onClick={runPreflight}
                  disabled={rerunning || !canManage}
                  className="inline-flex h-[36px] items-center gap-1 rounded-[8px] border border-line-strong bg-surface px-4 text-sm font-medium text-ink shadow-[0_1px_2px_rgba(82,88,102,0.06)] transition-colors hover:bg-bg disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {rerunning ? "Running…" : "Re-run All Checks"}
                </button>
              </div>
            </TabsContent>
          )}

          {tab === "history" && (
            <TabsContent value="history">
              <div className="mb-4 flex items-center gap-3">
                <h2 className="text-base font-bold text-ink">Deployment history</h2>
                <Badge variant="neutral">{DEP_TABLE.length} deployments</Badge>
              </div>
              <TableWrapper>
                <Table>
                  <thead>
                    <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                      <th className="px-4 py-2.5">ID</th>
                      <th className="px-4 py-2.5">Customer</th>
                      <th className="px-4 py-2.5">State</th>
                      <th className="px-4 py-2.5 text-right">Last Updated</th>
                      <th className="px-4 py-2.5 w-10" aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {DEP_TABLE.map((d) => (
                      <tr key={d.id} className="border-b border-line last:border-b-0 hover:bg-bg">
                        <td className="px-4 py-3 text-sm font-medium text-ink">{d.id}</td>
                        <td className="px-4 py-3 text-sm text-text">{d.customer}</td>
                        <td className="px-4 py-3">
                          <Badge variant={(d.badgeVariant as "info" | "success") ?? "neutral"}>{d.state}</Badge>
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-muted whitespace-nowrap">{d.lastUpdated}</td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            aria-label={`Open actions for ${d.id}`}
                            className="grid size-7 place-items-center rounded-md text-muted transition-colors hover:bg-line hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft"
                          >
                            <span aria-hidden className="text-lg leading-none">…</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrapper>
            </TabsContent>
          )}

          {tab !== "preflight" && tab !== "history" && (
            <div className="rounded-lg border border-line bg-surface px-4 py-10 text-center text-sm text-muted">
              {tab.charAt(0).toUpperCase() + tab.slice(1)} step — content to be implemented in the next scaling phase.
            </div>
          )}
        </div>
      </Tabs>
    </div>
  );
}